import type React from 'react';
import {
  useCaeEnforcement,
  useConsumptionsAndEmissionsFromOneGraphWithLoading,
  useCaeMissingLogger,
  type AcsResult,
  type GfaServiceConfigLike,
} from '@oneaudi/feature-app-utils';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { Logger } from '@feature-hub/logger';
import { hasValidCaeContent } from '../utils/caeUtils';

interface CaeGateProps {
  content:
    | {
        consumptionAndEmissions?: string[];
      }
    | null
    | undefined;
  localeService: LocaleServiceV1;
  oneGraphService: OneGraphServiceV1;
  loggerService?: Logger;
  acsResult?: AcsResult;
  gfaServiceConfigProvider?: GfaServiceConfigLike;
  children: React.ReactNode;
}

export const CaeGate: React.FC<CaeGateProps> = ({
  content,
  localeService,
  oneGraphService,
  loggerService,
  acsResult,
  gfaServiceConfigProvider,
  children,
}) => {
  const carlineIds = (content?.consumptionAndEmissions ?? []).filter(Boolean);

  const { caeDataRequired } = useCaeEnforcement({
    gfaServiceConfigProvider: gfaServiceConfigProvider ?? { gfa: {} },
    acsResult,
  });

  const { data: caeData, loading } = useConsumptionsAndEmissionsFromOneGraphWithLoading({
    ids: carlineIds,
    localeService,
    oneGraphService,
    logger: loggerService,
  });

  const missingIds = carlineIds.filter((_, index) => !hasValidCaeContent(caeData[index]));

  // Cardinality check prevents false positive when query returns [] initially
  const hasCaeData =
    carlineIds.length === 0 || (caeData.length === carlineIds.length && missingIds.length === 0);

  const shouldHide = carlineIds.length > 0 && caeDataRequired && !loading && !hasCaeData;

  useCaeMissingLogger({
    enabled: shouldHide,
    logger: loggerService,
    market: localeService.countryCode,
    missingIds,
    pageUrl: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  if (carlineIds.length > 0 && caeDataRequired && loading) {
    return null;
  }

  if (shouldHide) {
    return null;
  }

  return children as React.ReactElement;
};
